import { useMutation, useQuery } from 'react-query'
import { ProjectStatus } from '../../common/constants'
import api from '../api'
import { useJobColumnInvalidator } from '../JobList/jobListApi'
import { JobWorkarea } from './workareaPickerModel'

export type SetJobStatusResponse = {
  wasSuccessful: boolean
  jobFound: boolean
  needsWorkareaSelection: boolean
  changed: boolean
  status: ProjectStatus | null
  message: string | null
}

export const setJobStatus = async (jobId: number, status: ProjectStatus, workareaIds?: number[]) => {
  return (await api.put<SetJobStatusResponse>(`/web/jobs/${jobId}/status`, { status, workareaIds })).data
}

export type JobWorkareaStatusesResponse = {
  jobFound: boolean
  jobId: number
  jobName: string | null
  takesStatusFromWorkareas: boolean
  workareas: JobWorkarea[]
}

export const getJobWorkareaStatuses = async (jobId: number) => {
  return (await api.get<JobWorkareaStatusesResponse>(`/web/jobs/${jobId}/workarea-statuses`)).data
}

export const useJobWorkareaStatuses = (jobId: number | undefined) => {
  return useQuery(['job-workarea-statuses', jobId], {
    queryFn: () => getJobWorkareaStatuses(jobId!),
    enabled: jobId !== undefined,
    /* No retries: behind them a failure is indistinguishable from a slow load, and the
       modal is blocking a move the user just made. Say it failed and let them retry. */
    retry: false
  })
}

export type SetEstimateLostResponse = {
  wasSuccessful: boolean
  jobFound: boolean
  changed: boolean
  lost: boolean
  message: string | null
}

export const setEstimateLost = async (jobId: number, lost: boolean) => {
  return (await api.put<SetEstimateLostResponse>(`/web/jobs/${jobId}/estimate-lost`, { lost })).data
}

type Options = {
  onFailure: (jobId: number, message: string) => void
}

/* The write returns before the listener has rebuilt the snapshot the board reads, so the
   first refetch usually still reports the old column. One later attempt gives the read
   model time to catch up. This is only a nudge — correctness comes from the board holding
   its optimistic move until the source column stops reporting the job. */
export const SNAPSHOT_CATCH_UP_MS = 2500

const failureMessage = (error: any, fallback: string) =>
  error?.response?.data?.message ?? fallback

export const useSetEstimateLostMutation = ({ onFailure }: Options) => {
  const jobColumns = useJobColumnInvalidator()

  return useMutation(
    ({ jobId, lost }: { jobId: number, lost: boolean }) => setEstimateLost(jobId, lost),
    {
      onError: (error: any, { jobId }) => {
        onFailure(jobId, failureMessage(error, 'The estimate could not be saved.'))
      },
      onSuccess: () => {
        jobColumns.invalidate()
        setTimeout(() => jobColumns.invalidate(), SNAPSHOT_CATCH_UP_MS)
      }
    }
  )
}

export const useSetJobStatusMutation = ({ onFailure }: Options) => {
  const jobColumns = useJobColumnInvalidator()

  return useMutation(
    ({ jobId, status, workareaIds }: { jobId: number, status: ProjectStatus, workareaIds?: number[] }) =>
      setJobStatus(jobId, status, workareaIds),
    {
      onError: (error: any, { jobId }) => {
        onFailure(jobId, failureMessage(error, 'The status could not be saved.'))
      },
      onSuccess: () => {
        jobColumns.invalidate()
        setTimeout(() => jobColumns.invalidate(), SNAPSHOT_CATCH_UP_MS)
      }
    }
  )
}
